import clsx from 'clsx';
import React, { useMemo } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { AppContextProvider, Env } from '@contexts/AppContext';
import useEventTracking from '@hooks/useEventTracking';
import type { Newsletter } from '@interfaces/homepage';
import { titv_path } from '@routes';
import { Page } from '@tracking';
import type { User } from '@utils/backend-api/currentUser';
import type { Newsletter as NewsletterAPIType } from '@utils/backend-api/newsletters';
import type { LiveStream, TITVEvent } from '@utils/backend-api/titv';
import { LeadTypes } from '@utils/enums';

import type { TITVNetwork } from '../Network/interfaces';
import NewsletterSignupCard from '../NewsletterSignupCard';
import NewslettersSignup from '../NewslettersSignUp';
import StreamingChannels from '../StreamingChannels';
import TITVMultistreamFeatureHero from './Hero';
import TITVMultistreamFeatureHeroItems from './HeroStreams';
import TITVMultistreamFeatureLiveStream from './LiveStream';
import TITVMultistreamFeatureNetworkSections from './NetworkSections';

interface TITVMultistreamFeatureProps {
  availableNewsletters?: NewsletterAPIType[];
  env: Env;
  isGdprRequired: boolean;
  initialCurrentUser?: User;
  inProgressStream?: LiveStream;
  isWebview: boolean;
  mostRecentStream?: TITVEvent;
  signupNewsletter?: Newsletter;
  titvNetworks?: TITVNetwork[];
}

const queryClient = new QueryClient();

const TITVMultistreamFeature = ({
  availableNewsletters,
  env,
  isGdprRequired,
  initialCurrentUser,
  inProgressStream,
  isWebview,
  mostRecentStream,
  signupNewsletter,
  titvNetworks,
}: TITVMultistreamFeatureProps) => {
  const { Track } = useEventTracking({
    page: Page.TITVMultistreamFeature,
  });

  const sortedNetworks = useMemo(() => {
    return [...(titvNetworks ?? [])].sort((a, b) => b.position - a.position);
  }, [titvNetworks]);

  const firstNetwork = sortedNetworks[0];
  const inProgressStreamNetworkId = inProgressStream?.networkId ?? undefined;

  return (
    <Track>
      <AppContextProvider
        initialCurrentUser={initialCurrentUser}
        isGdprRequired={isGdprRequired}
        isWebview={isWebview}
        env={env}
      >
        <QueryClientProvider client={queryClient}>
          <TITVMultistreamFeatureLiveStream inProgressStream={inProgressStream} />
          <main className="my-ti-container relative w-full overflow-x-hidden px-16 sm:px-28 [&+_aside]:mt-0">
            <div className="mx-auto flex w-full flex-col xl:max-w-container-lg">
              <div
                className={clsx(
                  'flex flex-col',
                  inProgressStreamNetworkId != null &&
                    inProgressStreamNetworkId !== firstNetwork?.id
                    ? 'order-3 border-0 border-t border-solid border-grayscale-charcoal pt-32 lg:pt-40'
                    : 'order-1'
                )}
                data-testid="titv-hero-section"
              >
                <TITVMultistreamFeatureHero
                  isInProgressStream={inProgressStreamNetworkId != null}
                  mostRecentStream={mostRecentStream}
                  titvNetwork={firstNetwork}
                />
                {firstNetwork && (
                  <TITVMultistreamFeatureHeroItems
                    mostRecentStreamId={mostRecentStream?.id}
                    network={firstNetwork.slug}
                    networkId={firstNetwork.id}
                    streams={firstNetwork?.streams ?? []}
                    title={firstNetwork.title}
                  />
                )}
              </div>
              {signupNewsletter && (
                <NewsletterSignupCard
                  className="order-2 my-32 !border-grayscale-charcoal pt-32 lg:mb-64 lg:mt-40 lg:pt-64"
                  ctaText="Sign up to"
                  leadType={LeadTypes.TITVLandingRailNewsletterSignup}
                  newsletter={signupNewsletter as Newsletter}
                  redirectToOnSuccess={titv_path()}
                  isHorizontal
                />
              )}
              <TITVMultistreamFeatureNetworkSections
                inProgressStreamNetworkId={inProgressStreamNetworkId}
                titvNetworks={sortedNetworks?.slice(1)}
              />
              <div className="order-4 mx-auto my-32 w-fit sm:my-40 lg:my-64">
                <StreamingChannels isHorizontalOnly />
              </div>
            </div>
          </main>
          {!initialCurrentUser && <NewslettersSignup newsletters={availableNewsletters ?? []} />}
        </QueryClientProvider>
      </AppContextProvider>
    </Track>
  );
};

export default TITVMultistreamFeature;
